import type {
  MfgPricingLayerBand,
  MfgPricingLayerColumn,
  MfgPricingLayerGridRow,
} from "@/lib/manufacturing/cogsTypes";
import {
  DEFAULT_SHELF_ALERT_PCT,
  shelfCostIsIncomplete,
  shelfVariance,
  shelfVarianceAttention,
  type ShelfVariance,
  type ShelfVarianceAttention,
} from "./cogsShelfVariance";

export type ShelfBandCompare = {
  calculated: number;
  shelf: number | null;
  variance: ShelfVariance;
};

export type ShelfVarianceReportLine = {
  key: string;
  name: string;
  code: string;
  size_band: string;
  series: string;
  roof_style: string;
  wholesale: ShelfBandCompare;
  msrp: ShelfBandCompare;
  wholesale_attention: ShelfVarianceAttention;
  msrp_attention: ShelfVarianceAttention;
};

export type ShelfVarianceSort = "largest" | "wholesale" | "msrp" | "name";

export type ShelfVarianceBandSummary = {
  above: number;
  below: number;
  match: number;
  missing: number;
  net_delta_usd: number;
};

export type ShelfVarianceStep = {
  label: string;
  band: MfgPricingLayerBand | "start";
  amount_usd: number | null;
  running_after: number | null;
};

function round2(amount: number): number {
  return Math.round(amount * 100) / 100;
}

function compare(calculated: number, shelf: number | null | undefined): ShelfBandCompare {
  return {
    calculated,
    shelf: shelf ?? null,
    variance: shelfVariance(calculated, shelf),
  };
}

export function shelfVarianceRowKey(row: Pick<MfgPricingLayerGridRow, "subject_kind" | "subject_id">): string {
  return `${row.subject_kind}:${row.subject_id}`;
}

export function shelfVarianceReportLine(
  row: MfgPricingLayerGridRow,
  thresholdPct = DEFAULT_SHELF_ALERT_PCT,
): ShelfVarianceReportLine {
  const wholesale = compare(row.wholesale_total_usd, row.shelf_wholesale_usd);
  const msrp = compare(row.msrp_total_usd, row.shelf_msrp_usd);
  const incomplete = shelfCostIsIncomplete(row);
  return {
    key: shelfVarianceRowKey(row),
    name: row.name,
    code: row.code,
    size_band: row.size_band,
    series: row.series,
    roof_style: row.roof_style,
    wholesale,
    msrp,
    wholesale_attention: shelfVarianceAttention(wholesale.variance, incomplete, thresholdPct),
    msrp_attention: shelfVarianceAttention(msrp.variance, incomplete, thresholdPct),
  };
}

export function shelfLineNeedsAttention(line: ShelfVarianceReportLine): boolean {
  return (
    line.wholesale_attention !== "quiet" ||
    line.msrp_attention !== "quiet"
  );
}

/** A product counts once. An alert on either price wins over a warning. */
export function countShelfAttention(lines: readonly ShelfVarianceReportLine[]): { alerts: number; warnings: number } {
  let alerts = 0;
  let warnings = 0;
  for (const line of lines) {
    const marks = [line.wholesale_attention, line.msrp_attention];
    if (marks.includes("alert")) alerts += 1;
    else if (marks.includes("warning")) warnings += 1;
  }
  return { alerts, warnings };
}

export function shelfVarianceIsGap(line: ShelfVarianceReportLine): boolean {
  return (
    line.wholesale.variance.tone === "up" ||
    line.wholesale.variance.tone === "down" ||
    line.msrp.variance.tone === "up" ||
    line.msrp.variance.tone === "down"
  );
}

function gapAmount(band: ShelfBandCompare): number {
  if (band.variance.tone !== "up" && band.variance.tone !== "down") return 0;
  return Math.abs(band.variance.delta_usd);
}

export function sortShelfVarianceLines(
  lines: readonly ShelfVarianceReportLine[],
  sort: ShelfVarianceSort,
): ShelfVarianceReportLine[] {
  const copy = [...lines];
  copy.sort((a, b) => {
    if (sort === "name") return a.name.localeCompare(b.name) || a.code.localeCompare(b.code);
    const left = sort === "wholesale" ? gapAmount(a.wholesale) : sort === "msrp" ? gapAmount(a.msrp) : Math.max(gapAmount(a.wholesale), gapAmount(a.msrp));
    const right = sort === "wholesale" ? gapAmount(b.wholesale) : sort === "msrp" ? gapAmount(b.msrp) : Math.max(gapAmount(b.wholesale), gapAmount(b.msrp));
    return right - left || a.name.localeCompare(b.name);
  });
  return copy;
}

export function summarizeShelfVariance(
  lines: readonly ShelfVarianceReportLine[],
  band: "wholesale" | "msrp",
): ShelfVarianceBandSummary {
  const summary: ShelfVarianceBandSummary = { above: 0, below: 0, match: 0, missing: 0, net_delta_usd: 0 };
  for (const line of lines) {
    const item = line[band];
    if (item.variance.tone === "up") summary.above += 1;
    else if (item.variance.tone === "down") summary.below += 1;
    else if (item.variance.tone === "flat") summary.match += 1;
    else summary.missing += 1;
    if (item.variance.tone === "up" || item.variance.tone === "down") {
      summary.net_delta_usd = round2(summary.net_delta_usd + item.variance.delta_usd);
    }
  }
  return summary;
}

/** Materials, each pricing column, and a round-up step when the band sum moved after the last column. */
export function shelfVarianceSteps(
  row: MfgPricingLayerGridRow,
  columns: readonly Pick<MfgPricingLayerColumn, "id" | "label">[],
): ShelfVarianceStep[] {
  const labels = new Map(columns.map((column) => [column.id, column.label]));
  const steps: ShelfVarianceStep[] = [
    {
      label: "Materials",
      band: "start",
      amount_usd: row.materials_cost_usd,
      running_after: row.materials_cost_usd,
    },
  ];
  for (const result of row.column_results) {
    steps.push({
      label: labels.get(result.column_id) || result.key,
      band: result.band,
      amount_usd: result.result_amount,
      running_after: result.running_after,
    });
  }
  appendRoundUp(steps, "wholesale", row.wholesale_total_usd);
  appendRoundUp(steps, "msrp", row.msrp_total_usd);
  return steps;
}

const STEP_ORDER: Record<ShelfVarianceStep["band"], number> = { start: 0, cogs: 1, wholesale: 2, msrp: 3 };

function appendRoundUp(steps: ShelfVarianceStep[], band: MfgPricingLayerBand, total: number): void {
  let index = -1;
  for (let i = 0; i < steps.length; i += 1) {
    if (steps[i].band === band) index = i;
  }
  if (index < 0) {
    for (let i = 0; i < steps.length; i += 1) {
      if (STEP_ORDER[steps[i].band] < STEP_ORDER[band]) index = i;
    }
  }
  if (index < 0) return;
  const baseline = steps[index].running_after;
  if (baseline == null) return;
  const delta = round2(total - baseline);
  if (Math.abs(delta) < 0.005) return;
  steps.splice(index + 1, 0, { label: "Round up", band, amount_usd: delta, running_after: total });
}
